/**
 * Formatting utilities for Frontend/Homepage module
 *
 * Pure functions for formatting prices, text, and other display values.
 */

import { mapGraphQLProductToProduct } from '../../Theme'

import type {
  Banner,
  HomepageBanner,
  HomepageReview,
  Product,
  MagentoProduct,
  ReviewCard,
} from '../types'

/**
 * Format price in Romanian locale (Lei)
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat('ro-RO', {
    style: 'currency',
    currency: 'Lei',
  }).format(price)
}

/**
 * Calculate discount amount between regular and special price
 */
export function calculateDiscount(
  regularPrice: number,
  specialPrice: number | null | undefined
): number {
  if (!specialPrice || specialPrice >= regularPrice) return 0
  return regularPrice - specialPrice
}

/**
 * Truncate text to max length with ellipsis
 */
export function truncateText(text: string, maxLength = 70): string {
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength - 3)}...`
}

/**
 * Truncate product name (alias for truncateText with 70 char default)
 */
export function truncateName(name: string): string {
  return truncateText(name, 70)
}

// =============================================================================
// HOMEPAGE TRANSFORMERS
// =============================================================================

/**
 * Transform HomepageBanner (snake_case) to Banner block type (camelCase)
 */
export function transformBannerToBannerBlock(banner: HomepageBanner): Banner {
  return {
    id: banner.id,
    title: banner.title ?? undefined,
    imageDesktop: banner.image_desktop ?? undefined,
    imageMobile: banner.image_mobile ?? undefined,
    link: banner.link ?? undefined,
    linkText: banner.link_text ?? undefined,
  }
}

/**
 * Transform array of HomepageBanners to Banner block types
 */
export function transformBannersToBannerBlock(banners: HomepageBanner[]): Banner[] {
  return banners.map(transformBannerToBannerBlock)
}

/**
 * Transform MagentoProduct to ProductCard Product type.
 *
 * Delegates to Theme's canonical mapGraphQLProductToProduct so homepage
 * sliders/grids share the exact same Product mapping as Catalog/Promotions
 * (reward points, gift badge, locker delivery, h2 name, stock flags, etc).
 */
export function transformMagentoProductToProduct(mp: MagentoProduct): Product {
  return mapGraphQLProductToProduct(mp)
}

/**
 * Transform array of MagentoProduct to ProductCard Product array
 */
export function transformMagentoProductsToProducts(products: MagentoProduct[]): Product[] {
  return products.map(transformMagentoProductToProduct)
}

// =============================================================================
// DATE FORMATTERS
// =============================================================================

/**
 * Format a datetime string to Romanian-friendly date: dd.mm.yyyy
 *
 * @example formatReviewDate('2025-03-15T10:30:00') → '15.03.2025'
 */
export function formatReviewDate(dateStr: string): string {
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return ''
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

// =============================================================================
// REVIEWS TRANSFORMERS
// =============================================================================

/**
 * Transform HomepageReview (snake_case) to ReviewCard (camelCase)
 */
export function transformReviewToCard(review: HomepageReview): ReviewCard {
  return {
    reviewId: review.review_id,
    nickname: review.nickname,
    summary: review.summary,
    text: review.text,
    formattedDate: formatReviewDate(review.created_at),
    rating: review.average_rating,
    product: {
      name: review.product.name,
      url: review.product.url,
      image: review.product.image,
    },
  }
}

/**
 * Transform array of HomepageReview to ReviewCard array
 */
export function transformReviewsToCards(reviews: HomepageReview[]): ReviewCard[] {
  return reviews.map(transformReviewToCard)
}

/**
 * Sanitize a user-supplied href to prevent javascript:/data:/vbscript: XSS.
 * Allows site-relative paths (/foo) and http/https absolute URLs only.
 */
export function safeHref(value: string | undefined): string | undefined {
  if (!value) return undefined
  if (value.startsWith('/') && !value.startsWith('//')) return value
  try {
    const u = new URL(value)
    if (u.protocol === 'http:' || u.protocol === 'https:') return u.toString()
  } catch {
    // malformed URL — treat as unsafe
  }
  return undefined
}
